'use client';
import {Box} from '@mui/material';
import Container from '@mui/material/Container';
import {styled} from '@mui/material/styles';
import {useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Section, SectionTitle} from '../primitives';
import {AmenityChip, type AmenityChipProps} from './AmenityChip';

export interface AmenitiesSectionProps {
  items: AmenityChipProps[];
  onViewAll?: () => void;
}

const ItemsRow = styled(Box)(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  rowGap: theme.spacing(1.75),
  columnGap: theme.spacing(1.25),
}));

export function AmenitiesSection({items, onViewAll}: AmenitiesSectionProps) {
  const {t} = useTranslation();
  const itemsRowRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [visibleRowsHeight, setVisibleRowsHeight] = useState<number>();

  useLayoutEffect(() => {
    const row = itemsRowRef.current;
    if (!row) {
      return;
    }
    const measure = () => {
      const chips = Array.from(row.children, chip => chip as HTMLElement);
      if (chips.length === 0) {
        setOverflowing(false);
        setVisibleRowsHeight(undefined);
        return;
      }
      const firstTop = chips[0].offsetTop;
      const rowTops = [...new Set(chips.map(chip => chip.offsetTop))].sort((a, b) => a - b);
      const visibleTops = new Set(rowTops.slice(0, 2));
      setOverflowing(rowTops.length > 2);
      setVisibleRowsHeight(
        Math.max(...chips.filter(chip => visibleTops.has(chip.offsetTop)).map(chip => chip.offsetTop + chip.offsetHeight)) - firstTop,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => {
      observer.disconnect();
    };
  }, [items]);

  return (
    <Section tone="raised">
      <Container>
        <SectionTitle as="h2" sx={{mb: 2}}>
          {t('amenities')}
        </SectionTitle>
        <ItemsRow ref={itemsRowRef} sx={{maxHeight: visibleRowsHeight, overflow: 'hidden'}}>
          {items.map((item, index) => (
            <AmenityChip key={`${item.label}-${index}`} icon={item.icon} label={item.label} />
          ))}
        </ItemsRow>
        {onViewAll && overflowing && (
          <Button variant="outlined-dark" onClick={onViewAll} sx={{mt: 3}}>
            {t('view_all')}
          </Button>
        )}
      </Container>
    </Section>
  );
}

AmenitiesSection.displayName = 'AmenitiesSection';
